import React from 'react'
import { useQuery } from '@tanstack/react-query'
import CensusFileTimeline from './CensusFileTimeline'
import { getCensusRunSteps } from './census-run-api'
import PersonTrace from './PersonTrace'
import ProcessingDetails from './ProcessingDetails'
import { CensusRunDto } from './types'

/**
 * Rendered only when a row is expanded, so the steps are fetched on demand
 * rather than for every row in the list.
 */
function ExpandedRunDetails({
  runId,
  channel,
  firstName,
  lastName,
  clientPublicKey,
  clientCompanyName,
}: {
  runId: string
  channel: CensusRunDto['channel']
  firstName: string
  lastName: string
  clientPublicKey?: string
  clientCompanyName?: string
}) {
  const { data, isPending, isError } = useQuery({
    queryKey: ['census-run-steps', runId],
    queryFn: () => getCensusRunSteps(runId),
    staleTime: 60 * 1000,
  })

  const steps = data ?? []
  return (
    // The tinted panel is what separates an expanded run from the rows around
    // it; each section inside stays white so it reads as a card on top.
    <div style={{ background: '#f1f5f7', padding: '16px 20px 4px' }}>
      {/* Tracing reads every row snapshot the run wrote, so it runs only when
          an admin has asked about a particular person. */}
      {(firstName !== '' || lastName !== '') && (
        <PersonTrace runId={runId} firstName={firstName} lastName={lastName} />
      )}
      {/* The 13 processing steps run in partner-census-graph, which only the
          API integration passes through. An AMA file never goes near them, so
          the section is left out rather than shown empty. */}
      {channel === 'API' && (
        <ProcessingDetails
          steps={steps.filter((step) => step.phase === 'PROCESSING')}
          isLoading={isPending}
          isError={isError}
          clientPublicKey={clientPublicKey}
          clientCompanyName={clientCompanyName}
        />
      )}
      <CensusFileTimeline
        steps={steps.filter((step) => step.phase === 'TIMELINE')}
        isLoading={isPending}
        isError={isError}
        runId={runId}
        channel={channel}
        clientPublicKey={clientPublicKey}
        clientCompanyName={clientCompanyName}
      />
    </div>
  )
}

export default ExpandedRunDetails
